import {
    SET_PLATFORM_TYPE,
    UPDATE_CURRENT_TIME,
    SET_BROWSER_TYPE,
    SET_NVR_TIMESTAMP,
    SET_NVR_TIMEZONE,
    SET_CLOUD_API_VERSION
  } from './types';

import axios from 'axios';
import { getApiUrl } from '../util/getServerInfo';
import { debugWarn } from '../util/debug';
import { formatClockInZone } from '../util/cloudTime';
import { getBackend } from '../backend';
  
  /**
   * Apply a cloudapi version named by a response header (veCall's
   * CLOUDAPI_VERSION_EVENT) to the cloud session, so the header and the system
   * info sheet follow the backend actually answering. Ignored without a cloud
   * session: the local adapter has none, and a login response is followed by
   * its own payload's version.
   */
  export const applyCloudApiVersion = (version) => {
    return (dispatch) => {
        let backend;
        try { backend = getBackend(); } catch (e) { return; }
        if (!backend || typeof backend.setBackendVersion !== 'function') return;
        backend.setBackendVersion(version);
        if (backend.getBackendVersion() === version) dispatch({ type: SET_CLOUD_API_VERSION, payload: version });
    };
  };

  export const getPlatform = () => {
    return (dispatch) => {
        let platform = navigator.platform;
        let type = '';
        if(platform.indexOf('Win') > -1) {
            type = 'Win'
        } else if(platform.indexOf('Mac') > -1) {
            type = 'Mac'
        } else if(platform.indexOf('iPad') > -1) {
            type = 'Ios'
        } else if(platform.indexOf('iPhone') > -1) {
            type = 'Ios'
        } else if(platform.indexOf('Android') > -1 || platform.indexOf('Linux') > -1 ) {
            type = 'Android'
        };

        dispatch({ type: SET_PLATFORM_TYPE, payload: type });
        return(type)
    };
};  

export const getBrowser = () => {
    return (dispatch) => {
        let platform = navigator.userAgent;
        let sBrowser = 'Unknown';
        if(platform.indexOf("Firefox") > -1) {
            sBrowser = "firefox";
        }
        if(platform.indexOf("Safari") > -1) {
            sBrowser = "safari";
        }
        if(platform.indexOf("Opera") > -1 || platform.indexOf("OPR") > -1 ) {
            sBrowser = "opera";
        } 
        if(platform.indexOf("Chrome") > -1) {
            sBrowser = "chrome";
        }
        if(platform.indexOf("Trident") > -1) {
            sBrowser = "explorer";
        }
        if(platform.indexOf("Edge") > -1) {
            sBrowser = "edge";
        }

        dispatch({ type: SET_BROWSER_TYPE, payload: sBrowser });
        return (sBrowser);
    };
};  

export const updateCurrentTime = () => {
    return {
        type: UPDATE_CURRENT_TIME
    }
}

// Set NVR timestamp from camera metadata (called from websocket)
export const setNvrTimestampFromCamera = (cameraTime) => {
    return (dispatch) => {
        try {
            // Camera time format: "02-05-2026 12:11:13"
            if (!cameraTime || typeof cameraTime !== 'string') return;

            const parts = cameraTime.split(' ');
            if (parts.length !== 2) return;

            const [datePart, timePart] = parts;
            const dateComponents = datePart.split('-');
            const timeComponents = timePart.split(':');

            if (dateComponents.length !== 3 || timeComponents.length !== 3) return;

            const [month, day, year] = dateComponents;
            const [hour, minute, second] = timeComponents;

            // Validate components are numbers
            if (isNaN(month) || isNaN(day) || isNaN(year) ||
                isNaN(hour) || isNaN(minute) || isNaN(second)) return;

            const datetime = `${month}/${day}/${year} ${hour}:${minute}:${second}`;
            const shortStamp = `${year}${month}${day}${hour}${minute}${second}`;

            dispatch({ type: SET_NVR_TIMESTAMP, nvrTimestamp: datetime, nvrTimestampShort: shortStamp });
        } catch (error) {
            // Silently fail - browser timer will continue as fallback
            debugWarn('Failed to parse camera timestamp:', error);
        }
    };
};

// NVR clock offset (ms) — difference between NVR time and local time.
// Synced via RPC, then used to tick locally for smooth display.
let _nvrOffsetMs = null;
const NVR_SYNC_INTERVAL = 600; // re-sync with the NVR every ~10 min; the local tick fills every second in between
let _nvrSyncCounter = 0;

// Format a Date object into our display strings
const _formatNvrDate = (d) => {
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    const hour = ('0' + d.getHours()).slice(-2);
    const minute = ('0' + d.getMinutes()).slice(-2);
    const second = ('0' + d.getSeconds()).slice(-2);
    return {
        datetime: `${month}/${day}/${year} ${hour}:${minute}:${second}`,
        shortStamp: `${year}${month}${day}${hour}${minute}${second}`
    };
};

export const getNvrTimestamp = (sSess, sServer) => {
    return async (dispatch) => {
        _nvrSyncCounter++;
        const needsSync = _nvrOffsetMs === null || _nvrSyncCounter >= NVR_SYNC_INTERVAL;

        if (needsSync) {
            // Full RPC sync — fetch NVR time and calculate offset
            const reqBody = {
                "jsonrpc": 2.0,
                "method": "config.dtime.getAll",
                "id": 200,
                "params": [sSess]
            };

            try {
                const beforeMs = Date.now();
                const response = await axios({
                    method: 'post',
                    url: getApiUrl(sServer),
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    data: reqBody,
                    timeout: 2000
                });
                const rttMs = Date.now() - beforeMs;

                const result = response.data.result[1];
                const data = result.sTimestamp;
                const nvrDate = new Date(
                    parseInt(data.slice(0,4)), parseInt(data.slice(4,6)) - 1,
                    parseInt(data.slice(6,8)), parseInt(data.slice(8,10)),
                    parseInt(data.slice(10,12)), parseInt(data.slice(12,14))
                );

                // Offset = NVR time - local time (adjusted for half RTT)
                _nvrOffsetMs = nvrDate.getTime() - (beforeMs + Math.round(rttMs / 2));
                _nvrSyncCounter = 0;

                // Store timezone on sync
                if (result.sTimezone) {
                    dispatch({
                        type: SET_NVR_TIMEZONE,
                        nvrTimezone: result.sTimezone,
                        nvrDateFormat: result.sDateFormat
                    });
                }
            } catch (error) {
                // If sync fails and we have no offset yet, fall through to local tick
                if (_nvrOffsetMs === null) {
                    _nvrOffsetMs = 0; // assume NVR time ≈ local time
                }
            }
        }

        // Local tick — compute NVR time from local clock + offset
        const nvrNow = new Date(Date.now() + _nvrOffsetMs);
        const { datetime, shortStamp } = _formatNvrDate(nvrNow);
        dispatch({ type: SET_NVR_TIMESTAMP, nvrTimestamp: datetime, nvrTimestampShort: shortStamp });
    }
}

// Cloud has no NVR clock to poll, so the header/rail clock is a local tick. It now renders in the
// SITE zone (JSCustomerProject.sTimezone, seeded to state.utility.nvrTimezone at cloud login —
// #86bbm5p92) so the clock AND the playback "now"/defaults (nvrTimestampShort) are site-local, not
// browser-local — correct for a cross-zone viewer. Falls back to browser-local when no site zone is
// set (the unset-tz decision). Never calls the NVR RPC (would trip noauth→EXPIRE_SESSION on cloud).
// Seed the site timezone (JSCustomerProject.sTimezone) from the cloud session into state so the
// header/rail clock + playback "now" render in the site zone. Called on LiveView mount, so it covers
// BOTH a fresh login AND a cached-session RESUME (loginCloud's tail only fires on fresh login, so a
// page refresh would otherwise revert to browser-local). Safe no-op when no site zone is set. #86bbm5p92.
export const seedCloudTimezone = () => (dispatch) => {
    try {
        const backend = getBackend();
        const tz = (backend && backend.getPrimaryTimezone) ? backend.getPrimaryTimezone() : '';
        dispatch({ type: SET_NVR_TIMEZONE, nvrTimezone: tz || '', nvrDateFormat: '' });
    } catch (e) { /* leave the browser-local fallback in place */ }
};

export const setLocalTimestamp = () => {
    return (dispatch, getState) => {
        const tz = (getState().utility && getState().utility.nvrTimezone) || '';
        let datetime, shortStamp;
        if (tz) {
            const { date, time } = formatClockInZone(new Date(), tz);   // 'MM/DD/YYYY', 'HH:MM:SS'
            const [mo, da, yr] = date.split('/');
            const [hh, mi, ss] = time.split(':');
            datetime = `${date} ${time}`;
            shortStamp = `${yr}${mo}${da}${hh}${mi}${ss}`;
        } else {
            ({ datetime, shortStamp } = _formatNvrDate(new Date()));   // no site zone → browser-local
        }
        dispatch({ type: SET_NVR_TIMESTAMP, nvrTimestamp: datetime, nvrTimestampShort: shortStamp });
    };
};